import React, { useState } from "react";
import axios from "axios";
import { Toaster, toast } from "sonner";
import {
  LineChart,
  Target,
  HeartPulse,
  Users,
  MessageCircle,
  Mail,
  Phone,
  Quote as QuoteIcon,
} from "lucide-react";

const PORTRAIT =
  "https://customer-assets-agu9un31.emergentagent.net/job_face-feature-land/artifacts/arsooa6i_image.png";
const QR_HABLEMOS =
  "https://customer-assets.emergentagent.com/job_face-feature-land/artifacts/9kcigw47_image.png";
const INVERSIMPLY_LOGO =
  "https://customer-assets-agu9un31.emergentagent.net/job_face-feature-land/artifacts/6f9q760x_image.png";

const WHATSAPP_NUMBER = "+34606635155";
const WHATSAPP_DISPLAY = "+34 606 635 155";
const EMAIL = "pablo.perezfm@inversimply.com";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const services = [
  {
    icon: LineChart,
    title: "Análisis financiero 100 % gratuito",
    body:
      "Revisamos tu situación real —ingresos, gastos, ahorro e inversión— sin coste ni compromiso.",
  },
  {
    icon: Target,
    title: "Un plan a tu medida",
    body:
      "Planificación personalizada para que tu dinero trabaje según tus objetivos y tu ritmo de vida.",
  },
  {
    icon: HeartPulse,
    title: "Chequeo de salud financiera",
    body:
      "Ideal si quieres empezar desde cero o comprobar si vas por el buen camino.",
  },
  {
    icon: Users,
    title: "Explicaciones y formaciones para grupos y familias",
    body:
      "Sesiones dinámicas y didácticas para tu grupo, familia o comunidad. Aprender de dinero, en compañía.",
  },
];

const testimonials = [
  {
    name: "I. Álvarez",
    text:
      "Me enseñó la realidad sobre las pensiones y cómo afrontarlo por nosotros mismos para tener una jubilación plena y tranquila.",
  },
  {
    name: "JR. Serrano",
    text:
      "Me enseñó caminos distintos y mucho más eficientes para acortar mi hipoteca, así ganarle la partida a mi banco.",
  },
  {
    name: "C. Alzate",
    text:
      "Pablo no me trató como suelen hacerlo en los bancos al uso. Analizó mi situación, me recomendó distintas estrategias para invertir y me enseñó todas las carteras a las que tiene acceso y cuál era la que más se adaptaba a lo que busco.",
  },
];

/**
 * Inversimply logo — brand mark of the current firm Pablo works with.
 */
function InversimplyLogo({ className = "", size = 56 }) {
  return (
    <img
      src={INVERSIMPLY_LOGO}
      alt="Inversimply"
      data-testid="inversimply-logo"
      className={className}
      style={{ height: size, width: "auto" }}
    />
  );
}

export default function LandingPage() {
  const whatsappHref = `https://wa.me/${WHATSAPP_NUMBER.replace(
    /\D/g,
    ""
  )}?text=${encodeURIComponent(
    "Hola Pablo, me gustaría reservar una sesión gratuita."
  )}`;
  const mailHref = `mailto:${EMAIL}?subject=${encodeURIComponent(
    "Reserva sesión gratuita"
  )}`;

  const [form, setForm] = useState({ name: "", contact: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const onChange = (k) => (e) =>
    setForm((prev) => ({ ...prev, [k]: e.target.value }));

  const submitLead = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.contact.trim()) {
      toast.error("Añade tu nombre y un contacto (email o teléfono).");
      return;
    }
    try {
      setSubmitting(true);
      await axios.post(`${API}/leads`, {
        name: form.name.trim(),
        contact: form.contact.trim(),
        message: form.message.trim(),
      });
      toast.success("¡Solicitud enviada! Pablo te contactará pronto.");
      setSent(true);
      setForm({ name: "", contact: "", message: "" });
    } catch (err) {
      toast.error("No se pudo enviar. Prueba WhatsApp o email.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="min-h-screen w-full bg-[color:var(--navy)] text-white"
      data-testid="landing-root"
    >
      <Toaster
        position="top-center"
        toastOptions={{
          style: {
            background: "#ffffff",
            color: "#0d1e3c",
            border: "1px solid rgba(13,30,60,0.2)",
            fontFamily: "'Times New Roman', Times, serif",
            fontSize: "16px",
          },
        }}
      />
      {/* ============ HERO ============ */}
      <section
        id="inicio"
        data-testid="hero-section"
        className="relative w-full px-6 md:px-12 lg:px-20 pt-8 pb-16 md:pt-10 md:pb-24"
      >
        {/* Top bar: brand logo + nav CTAs */}
        <div className="flex items-center justify-between mb-14 md:mb-20">
          <InversimplyLogo size={72} />
          <a
            href="#reserva"
            data-testid="nav-cta"
            className="hidden sm:inline-flex items-center gap-2 px-5 py-2.5 bg-white text-[color:var(--navy)] rounded-sm text-base hover:bg-[color:var(--sky)] transition-colors"
          >
            Reservar sesión
          </a>
        </div>

        {/* Small kicker */}
        <div
          className="text-[color:var(--sky)] tracking-[0.25em] text-xs md:text-sm uppercase mb-6 md:mb-8"
          data-testid="hero-kicker"
        >
          Asesoría financiera gratuita e individualizada
        </div>

        {/* Two-column: title/subtitle + portrait */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          {/* Left: title */}
          <div className="lg:col-span-7">
            <h1
              data-testid="hero-title"
              className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl leading-[1.05] font-normal text-white"
            >
              Gestiona tus finanzas con claridad y control.
            </h1>

            <p
              data-testid="hero-subtitle"
              className="mt-8 text-lg md:text-xl text-[color:var(--sky-2)] leading-relaxed max-w-2xl"
            >
              Análisis y planificación financiera 100 % gratuita,
              individualizada y sin compromiso.
            </p>

            {/* CTAs */}
            <div className="mt-10 flex flex-wrap gap-4">
              <a
                href="#reserva"
                data-testid="hero-cta-primary"
                className="inline-flex items-center gap-2 px-7 py-4 bg-white text-[color:var(--navy)] rounded-sm text-lg hover:bg-[color:var(--sky)] transition-colors"
              >
                Reservar sesión gratuita
              </a>
              <a
                href="#servicios"
                data-testid="hero-cta-secondary"
                className="inline-flex items-center gap-2 px-7 py-4 border border-white text-white rounded-sm text-lg hover:bg-white hover:text-[color:var(--navy)] transition-colors"
              >
                Ver servicios
              </a>
            </div>
          </div>

          {/* Right: portrait circular + quote */}
          <div className="lg:col-span-5 flex flex-col items-center lg:items-start">
            <div className="relative">
              <img
                src={PORTRAIT}
                alt="Pablo Pérez, agente financiero"
                data-testid="hero-portrait"
                className="w-64 md:w-80 h-auto"
              />
            </div>

            <div className="mt-6 text-center lg:text-left max-w-sm">
              <div className="text-xl md:text-2xl text-white">Pablo Pérez</div>
              <div className="text-[color:var(--sky)] text-base md:text-lg italic">
                Agente financiero — Inversimply
              </div>
              <p className="mt-4 text-[color:var(--sky-2)] text-base md:text-lg italic leading-relaxed">
                “Creo que la educación financiera debería estar al alcance de
                todos. Por eso, junto a Inversimply, comparto mi
                experiencia de forma gratuita con quien quiera dar el primer
                paso.”
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ============ SERVICIOS ============ */}
      <section
        id="servicios"
        data-testid="services-section"
        className="w-full px-6 md:px-12 lg:px-20 py-16 md:py-24 bg-[color:var(--navy-2)]"
      >
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-14 md:mb-20">
            <div className="text-[color:var(--sky)] tracking-[0.25em] text-xs md:text-sm uppercase mb-4">
              ¿Qué te ofrecemos?
            </div>
            <h2
              data-testid="services-title"
              className="text-3xl md:text-5xl text-white"
            >
              Cuatro servicios, un solo compromiso: tu claridad financiera.
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-10">
            {services.map((s, i) => {
              const Icon = s.icon;
              return (
                <div
                  key={s.title}
                  data-testid={`service-card-${i + 1}`}
                  className="bg-[color:var(--navy-deep)]/60 rounded-lg p-8 md:p-10 text-left"
                >
                  <div className="w-16 h-16 rounded-full bg-[color:var(--sky)] flex items-center justify-center mb-6">
                    <Icon
                      className="w-8 h-8 text-[color:var(--navy)]"
                      strokeWidth={1.6}
                    />
                  </div>
                  <h3 className="text-2xl md:text-3xl text-white mb-3 leading-tight">
                    {s.title}
                  </h3>
                  <p className="text-[color:var(--sky-2)] text-base md:text-lg leading-relaxed">
                    {s.body}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ============ TESTIMONIOS ============ */}
      <section
        id="testimonios"
        data-testid="testimonials-section"
        className="w-full px-6 md:px-12 lg:px-20 py-16 md:py-24"
      >
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-14 md:mb-16">
            <div className="text-[color:var(--sky)] tracking-[0.25em] text-xs md:text-sm uppercase mb-4">
              Testimonios
            </div>
            <h2
              data-testid="testimonials-title"
              className="text-3xl md:text-5xl text-white"
            >
              Lo que dicen quienes ya dieron el primer paso.
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            {testimonials.map((t, i) => (
              <div
                key={t.name}
                data-testid={`testimonial-${i + 1}`}
                className="bg-[color:var(--navy-2)] rounded-lg p-8 md:p-10 flex flex-col"
              >
                <QuoteIcon
                  className="w-8 h-8 text-[color:var(--sky)] mb-6"
                  strokeWidth={1.4}
                />
                <p className="text-white text-lg md:text-xl leading-relaxed italic flex-1">
                  “{t.text}”
                </p>
                <div className="mt-8 pt-6 border-t border-white/10 text-[color:var(--sky)] text-base md:text-lg">
                  {t.name}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ RESERVA ============ */}
      <section
        id="reserva"
        data-testid="reserva-section"
        className="w-full px-6 md:px-12 lg:px-20 py-20 md:py-32 text-center"
      >
        <div className="max-w-3xl mx-auto">
          <div className="text-[color:var(--sky)] tracking-[0.25em] text-xs md:text-sm uppercase mb-4">
            Reserva tu sesión gratuita
          </div>
          <h2
            data-testid="reserva-title"
            className="text-4xl md:text-6xl text-white leading-tight"
          >
            Da el primer paso hoy.
          </h2>
          <p className="mt-6 text-lg md:text-xl text-[color:var(--sky-2)] italic">
            Sin compromiso · Plazas limitadas este mes
          </p>

          {/* Lead form */}
          <form
            onSubmit={submitLead}
            data-testid="lead-form"
            className="mt-12 mx-auto max-w-2xl text-left bg-[color:var(--navy-2)] rounded-lg p-6 md:p-10"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
              <label className="block">
                <span className="block text-[color:var(--sky)] text-sm mb-2">
                  Nombre
                </span>
                <input
                  data-testid="lead-name"
                  type="text"
                  value={form.name}
                  onChange={onChange("name")}
                  placeholder="Ej. María López"
                  className="w-full bg-[color:var(--navy-deep)] text-white placeholder:text-white/40 border border-white/15 focus:border-[color:var(--sky)] outline-none rounded-sm px-4 py-3 text-base"
                  required
                />
              </label>
              <label className="block">
                <span className="block text-[color:var(--sky)] text-sm mb-2">
                  Email o teléfono
                </span>
                <input
                  data-testid="lead-contact"
                  type="text"
                  value={form.contact}
                  onChange={onChange("contact")}
                  placeholder="tú@ejemplo.com  ·  +34 6XX XXX XXX"
                  className="w-full bg-[color:var(--navy-deep)] text-white placeholder:text-white/40 border border-white/15 focus:border-[color:var(--sky)] outline-none rounded-sm px-4 py-3 text-base"
                  required
                />
              </label>
            </div>
            <label className="block mt-4 md:mt-5">
              <span className="block text-[color:var(--sky)] text-sm mb-2">
                ¿En qué quieres que te ayude? (opcional)
              </span>
              <textarea
                data-testid="lead-message"
                value={form.message}
                onChange={onChange("message")}
                rows={3}
                placeholder="Ahorro, hipoteca, inversión, jubilación…"
                className="w-full bg-[color:var(--navy-deep)] text-white placeholder:text-white/40 border border-white/15 focus:border-[color:var(--sky)] outline-none rounded-sm px-4 py-3 text-base resize-none"
              />
            </label>

            <div className="mt-6 flex flex-col sm:flex-row items-center justify-between gap-4">
              <p className="text-white/60 text-sm">
                Al enviar aceptas que Pablo te contacte para agendar la sesión.
              </p>
              <button
                type="submit"
                data-testid="lead-submit"
                disabled={submitting}
                className="inline-flex items-center gap-2 px-8 py-3.5 bg-white text-[color:var(--navy)] rounded-sm text-lg hover:bg-[color:var(--sky)] transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {submitting
                  ? "Enviando…"
                  : sent
                  ? "Enviado ✓"
                  : "Enviar solicitud"}
              </button>
            </div>
          </form>
        </div>
      </section>

      {/* ============ FOOTER ============ */}
      <footer
        data-testid="site-footer"
        className="w-full px-6 md:px-12 lg:px-20 py-8 bg-[color:var(--navy-deep)]"
      >
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4 text-center md:text-left">
          <div className="flex items-center gap-4">
            <InversimplyLogo size={56} className="opacity-95" />
            <div className="text-[color:var(--sky-2)] text-sm md:text-base">
              Pablo Pérez — Inversimply
            </div>
          </div>
          <div className="flex items-center gap-6 text-[color:var(--sky-2)] text-sm md:text-base">
            <a
              href={`tel:${WHATSAPP_NUMBER}`}
              data-testid="footer-phone"
              className="inline-flex items-center gap-2 hover:text-white"
            >
              <Phone className="w-4 h-4" /> {WHATSAPP_DISPLAY}
            </a>
            <a
              href={mailHref}
              data-testid="footer-email"
              className="inline-flex items-center gap-2 hover:text-white"
            >
              <Mail className="w-4 h-4" /> {EMAIL}
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}
